import {
  BUSINESS_FEATURE_IDS,
  MODULE_CLASS_DEFINITIONS,
  MODULE_COMPONENT_DEFINITIONS,
  PROFESSIONAL_MODULE_DEFINITIONS,
  componentDefinitionsFor,
  featureDefinitionsFor,
  professionalModuleDefinition,
} from '../common/adminModuleCatalog.js';
import {api} from './api.js';
import type {AuthSnapshot} from './auth.js';
import {apiUrl} from './config.js';
import {escapeHtml} from './layout.js';

type ModuleSwitchTarget = {
  id: string;
  name: string;
  enabled: boolean;
  kind: 'professional' | 'component' | 'feature';
  impact: string;
};

type CatalogProduct = {id: string; name: string; enabled: boolean};
type CatalogComponent = {
  id: string;
  name: string;
  version: string;
  enabled: boolean;
  kind: 'runtime' | 'task-gate';
  summary: string;
  impact: string;
  usedBy: string[];
  usedByNames: string[];
};
type CatalogProfessional = {
  id: string;
  name: string;
  version: string;
  enabled: boolean;
  registered: boolean;
  summary: string;
  capabilities: string[];
  entitlementPolicyLabel: string;
  serviceLinks: Array<{label: string; href: string}>;
  products: CatalogProduct[];
  usage: {personalSubscriptions: number; enterpriseSubscriptions: number; enterpriseSeats: number; listed: boolean};
  components: CatalogComponent[];
  features?: Array<{id: string; name: string; enabled: boolean; impact: string}>;
};
type ModulesPayload = {
  modules?: Array<{id: string; name: string; version: string; enabled: boolean; updatedAt?: string}>;
  catalog?: {
    classes?: Array<{id: string; name: string; summary: string}>;
    professional: CatalogProfessional[];
    platform?: CatalogComponent[];
    unassignedComponents: CatalogComponent[];
    summary: {
      professionalEnabled: number;
      platformEnabled?: number;
      productsListed: number;
      personalSubscriptions: number;
      enterpriseSeats: number;
    };
  };
};

/**
 * 旧版接口仅返回 modules 时，用静态目录拼出同等结构。
 * @param modules 系统模块列表
 */
function catalogFromModules(modules: Array<{id: string; name: string; version: string; enabled: boolean}>): NonNullable<ModulesPayload['catalog']> {
  const byId = new Map(modules.map((item) => [item.id, item]));
  const professional = PROFESSIONAL_MODULE_DEFINITIONS.map((definition) => {
    const recorded = byId.get(definition.id);
    const components = componentDefinitionsFor(definition.id)
      .map((item) => {
        const recordedComponent = byId.get(item.id);
        return recordedComponent
          ? {
            id: recordedComponent.id,
            name: recordedComponent.name,
            version: recordedComponent.version,
            enabled: recordedComponent.enabled !== false,
            kind: item.kind,
            summary: item.summary,
            impact: item.impact,
            usedBy: item.usedBy,
            usedByNames: item.usedBy.map((id) => professionalModuleDefinition(id)?.name ?? id),
          }
          : null;
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
    return {
      id: definition.id,
      name: recorded?.name ?? definition.name,
      version: recorded?.version ?? '1.0.0',
      enabled: recorded?.enabled !== false,
      registered: Boolean(recorded),
      summary: definition.summary,
      capabilities: definition.capabilities,
      entitlementPolicyLabel: definition.entitlementPolicyLabel,
      serviceLinks: definition.serviceLinks,
      products: [],
      usage: {personalSubscriptions: 0, enterpriseSubscriptions: 0, enterpriseSeats: 0, listed: false},
      components,
      features: featureDefinitionsFor(definition.id).map((item) => ({
        id: item.id,
        name: item.name,
        enabled: byId.get(item.id)?.enabled !== false,
        impact: item.impact,
      })),
    } satisfies CatalogProfessional;
  });
  const assigned = new Set([
    ...professional.map((item) => item.id),
    ...BUSINESS_FEATURE_IDS,
    ...MODULE_COMPONENT_DEFINITIONS.map((item) => item.id),
  ]);
  const unassignedComponents = modules
    .filter((item) => !assigned.has(item.id))
    .map((item) => ({
      id: item.id,
      name: item.name,
      version: item.version,
      enabled: item.enabled !== false,
      kind: 'runtime' as const,
      summary: item.name,
      impact: '未登记到分类目录。关闭可能影响未说明的运行时能力。',
      usedBy: [],
      usedByNames: [],
    }));
  const platformById = new Map<string, CatalogComponent>();
  for (const item of professional.flatMap((module) => module.components)) platformById.set(item.id, item);
  for (const item of unassignedComponents) if (!platformById.has(item.id)) platformById.set(item.id, item);
  const platform = [...platformById.values()];
  return {
    classes: MODULE_CLASS_DEFINITIONS,
    professional,
    platform,
    unassignedComponents,
    summary: {
      professionalEnabled: professional.filter((item) => item.enabled).length,
      platformEnabled: platform.filter((item) => item.enabled).length,
      productsListed: 0,
      personalSubscriptions: 0,
      enterpriseSeats: 0,
    },
  };
}

/**
 * 渲染系统开关：超级管理员可拨，运营人员只读。
 * @param target 开关目标
 * @param canEdit 是否超级管理员
 */
function renderModuleSwitch(target: ModuleSwitchTarget, canEdit: boolean): string {
  const on = target.enabled;
  const payload = encodeURIComponent(JSON.stringify(target));
  const state = on ? '已启用' : '已停用';
  if (!canEdit) {
    return `<span class="module-switch is-readonly ${on ? 'is-on' : 'is-off'}" role="status" aria-label="${state}"><span class="module-switch-track"><span class="module-switch-knob"></span></span></span>`;
  }
  return `<button class="module-switch ${on ? 'is-on' : 'is-off'}" type="button" role="switch" aria-checked="${on ? 'true' : 'false'}" aria-label="${state}${escapeHtml(target.name)}" data-module-switch="${payload}"><span class="module-switch-track"><span class="module-switch-knob"></span></span></button>`;
}

/**
 * 渲染业务功能卡片。
 * @param module 目录项
 * @param canEdit 是否超级管理员
 */
function renderProfessionalCard(module: CatalogProfessional, canEdit: boolean): string {
  const product = module.products[0];
  const listed = product ? (product.enabled ? '可售' : '停售') : '未绑定产品';
  const meta = [
    `${module.usage.personalSubscriptions} 人`,
    `${module.usage.enterpriseSeats} 席`,
    listed,
  ].join(' · ');
  const impact = `停用后所有客户端将无法进入该模块。当前 ${module.usage.personalSubscriptions} 人、${module.usage.enterpriseSeats} 个席位有效。`;
  const features = module.features?.length
    ? `<ul class="module-feature-list">${module.features.map((item) => `<li class="module-feature-row"><strong>${escapeHtml(item.name)}</strong>${renderModuleSwitch({id: item.id, name: item.name, enabled: item.enabled, kind: 'feature', impact: item.impact}, canEdit)}</li>`).join('')}</ul>`
    : '';
  return `<article class="module-catalog-card ${module.enabled ? 'is-enabled' : 'is-disabled'}">
    <header class="module-catalog-head">
      <div>
        <h2>${escapeHtml(module.name)}</h2>
        <p class="module-catalog-summary">${escapeHtml(module.capabilities.join('、'))}</p>
      </div>
      ${module.registered ? renderModuleSwitch({id: module.id, name: module.name, enabled: module.enabled, kind: 'professional', impact}, canEdit) : '<span class="status-tag warning">未登记</span>'}
    </header>
    <p class="module-catalog-meta">${escapeHtml(meta)}</p>
    ${features}
  </article>`;
}

/**
 * 渲染平台基础架构行。
 * @param item 基础架构项
 * @param canEdit 是否超级管理员
 */
function renderPlatformRow(item: CatalogComponent, canEdit: boolean): string {
  const usedBy = item.usedByNames.filter(Boolean).join('、');
  return `<li class="module-platform-row">
    <div>
      <strong>${escapeHtml(item.name)}</strong>
      ${usedBy ? `<span>${escapeHtml(usedBy)}</span>` : ''}
    </div>
    ${renderModuleSwitch({id: item.id, name: item.name, enabled: item.enabled, kind: 'component', impact: item.impact}, canEdit)}
  </li>`;
}

/**
 * 绑定模块总闸与组件开关。
 * @param host 页面容器
 * @param refresh 保存后刷新
 */
function bindModuleSwitches(host: HTMLElement, refresh: () => Promise<void>): void {
  host.querySelectorAll<HTMLButtonElement>('[data-module-switch]').forEach((button) => {
    button.addEventListener('click', async () => {
      let target: ModuleSwitchTarget;
      try {
        target = JSON.parse(decodeURIComponent(button.dataset.moduleSwitch || '')) as ModuleSwitchTarget;
      } catch {
        return;
      }
      const nextEnabled = !target.enabled;
      const action = nextEnabled ? '启用' : '停用';
      if (!window.confirm(`确定${action}「${target.name}」吗？${target.impact}`)) return;
      button.disabled = true;
      try {
        await api.patch(apiUrl(`/api/admin/modules/${encodeURIComponent(target.id)}`), {enabled: nextEnabled});
        await refresh();
      } catch (error) {
        button.disabled = false;
        window.alert(error instanceof Error ? error.message : String(error));
      }
    });
  });
}

/**
 * 渲染后台模块管理页。
 * @param host 路由容器
 * @param auth 当前管理员
 */
export async function modulesPage(host: HTMLElement, auth: AuthSnapshot): Promise<void> {
  const data = await api.get<ModulesPayload>(apiUrl('/api/admin/modules'));
  const catalog = data.catalog ?? catalogFromModules(data.modules ?? []);
  const canEdit = auth.role === 'super_admin';
  const platformItems = (catalog.platform?.length ? catalog.platform : [...catalog.professional.flatMap((item) => item.components), ...catalog.unassignedComponents])
    .map((item) => ({
      ...item,
      summary: item.summary || item.impact,
      usedBy: item.usedBy ?? [],
      usedByNames: item.usedByNames ?? [],
    }));
  host.classList.add('modules-page');
  host.innerHTML = `<section class="module-class-section">
      <h2 class="module-class-title">业务功能</h2>
      <div class="module-catalog-grid">${catalog.professional.map((item) => renderProfessionalCard(item, canEdit)).join('')}</div>
    </section>
    <section class="module-class-section">
      <h2 class="module-class-title">平台基础架构</h2>
      <div class="admin-panel module-platform-panel">
        <ul class="module-platform-list">${platformItems.map((item) => renderPlatformRow(item, canEdit)).join('') || '<li class="module-platform-empty">暂无组件</li>'}</ul>
      </div>
    </section>`;
  bindModuleSwitches(host, () => modulesPage(host, auth));
}
